<style scoped>
@import '../../assets/css/common.css';
</style>
<template>
    <div>
        <Flag v-if="showMappedByEmailFlag" @close="hideMappedByEmailFlag" class="user-mappings-flag"
              :title="mapUsersByEmailResultMessage.title"
              :type="mapUsersByEmailResult" :description="mapUsersByEmailResultMessage.content"/>
        <Modal v-if="emailMappingModalOpened" heading="Map by e-mail" :actions="userMapByEmailModalActionNames"
               @submit="mapUserByEmailFromModal" @cancel="setOpenEmailMappingsModal(false)">
            <div slot="content">
                This will map users whose accounts on both systems share the same e-mail.
            </div>
        </Modal>


        <h1 class="user-mapping-title">Users Mapping</h1>
        <notification v-if="bulkMappingByEmailAvailable" class="map-by-email-notification info" icon-name="info-circle" icon-class="info-message">
            <p>You can map users one by one or try <a @click="setOpenEmailMappingsModal(true)">bulk mapping by
                e-mail.</a> If you are using Jira Cloud please make sure that users have
                <a target="_blank" href="https://apps.appf.re/t4j/web/unable-to-get-users-emails">
                public visible e-mails.</a></p>
            <p><b>Note: </b>in the latest Jira Server version this feature is no longer available. Please keep this in mind when you update Jira.</p>
        </notification>
        <div class="tfs4jira-mapping-container">
            <UsersMappingAdder 
                jiraSelectTitle="Jira Users" 
                tfsSelectTitle="TFS Users"
            />
            <div class="mapping-no-mapped-values-container" v-show="(mappings && mappings.length == 0)">
                <span class="tfs4jira-mapping-no-mapped-values">
                    Please select Jira and TFS users that you want to map.
                </span>
            </div>
            <table class="tfs4jira-mapping-table">
                <tbody ref="tbody" :class="{'scrollable-tbody': isTbodyScrollable}">
                    <mapping-row :key=row.Id v-for="row in mappings" v-bind:mapping="row"></mapping-row>
                </tbody>
            </table>
            <users-mapping-default :profileGuid="profileGuid" :contextPath="contextPath"
                                   :loadOnInit="loadDefaultValues"></users-mapping-default>
        </div>
        <div class="spinner" v-show="isLoaderVisible"></div>
    </div>
</template>
<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import axios from 'axios';
import { NAMESPACE as APP_NAMESPACE } from '../../store/modules/app.js';
import { NAMESPACE as MAPPINGS_NAMESPACE } from '../../store/modules/mappings.js';
import { NAMESPACE as USERS_MAPPING_NAMESPACE } from '../../store/modules/usersMapping.js';
import UsersMappingAdder from './UsersMappingAdder';
import MappingRow from '../mappings/MappingRow';
import UsersMappingDefault from './UsersMappingDefault';
import UserMappingsService from '../../service/usersMapping.js';
import Notification from "../Notification";
import { Flag, Modal } from '@spartez/vue-atlaskit';

const cellHeight = 62;
const cellsVisibleAtOnce = 5;
const scrollableTbodyMinHeight = cellHeight * cellsVisibleAtOnce;

export default {
    components: {
        UsersMappingAdder,
        MappingRow,
        UsersMappingDefault,
        Notification,
        Flag,
        Modal
    },
    props: [
        'profileGuid',
        'loadOnInit'
    ],
    data() {
        return {
            loadDefaultValues: this.loadOnInit,
            mountCallback: function () {
            }, // its for unit tests purposes
            emailMappingModalOpened: false,
            userMapByEmailModalActionNames: ["Bulk map by e-mail", "Cancel"],
            isTbodyScrollable: false,
            service: null,
            bulkMappingByEmailAvailable: false,
        }
    },
    watch: {
        mappings(newVal, oldVal) {
            this.$nextTick(() => {
                if(this.$refs.tbody.scrollHeight >= scrollableTbodyMinHeight) {
                    this.isTbodyScrollable = true;
                } else {
                    this.isTbodyScrollable = false;
                }
            })
        }
    },
    computed: {
        ...mapGetters({
            mappings: MAPPINGS_NAMESPACE.getters.MAPPINGS,
            isLoaderVisible: MAPPINGS_NAMESPACE.getters.IS_LOADER_VISIBLE,
            contextPath: APP_NAMESPACE.getters.CONTEXT_PATH
        }),
        ...mapGetters('usersMapping', ['showMappedByEmailFlag', 'mapUsersByEmailResult', 'mapUsersByEmailResultMessage']),
    },
    mounted: function () {
        if (this.loadOnInit !== undefined && this.loadOnInit === 'true') {
            this.initComponent();
        }
        this.registerListener({
            on: 'RefreshUsersMappingComponent',
            handler: function (notification) {
                this.initComponent();
            }.bind(this)
        });
    },
    methods: {
        ...mapActions({
            setUpService: MAPPINGS_NAMESPACE.actions.SET_UP_SERVICE,
            getAvailableValues: MAPPINGS_NAMESPACE.actions.GET_AVAILABLE_VALUES,
            registerListener: APP_NAMESPACE.actions.ADD_LISTENER,
            setAvailableValuesForDefaults: USERS_MAPPING_NAMESPACE.actions.SET_AVAILABLE_VALUES,
        }),
        ...mapActions('usersMapping',
            ['mapByEmail']
        ),
        ...mapMutations('usersMapping', ['hideMappedByEmailFlag']),
        initComponent: async function () {
            this.service = new UserMappingsService(axios);
            this.setUpService({
                prefix: this.contextPath + '/api/profiles/' + this.profileGuid,
                service: this.service
            });
            let dataFromApi = await this.getAvailableValues();
            this.bulkMappingByEmailAvailable = dataFromApi.bulkMappingByEmailAvailable;
            this.loadDefaultValues = 'true';

            this.setAvailableValuesForDefaults(dataFromApi.available);
            this.mountCallback();
        },
        setOpenEmailMappingsModal: function (value) {
            this.emailMappingModalOpened = value;
        },
        mapUserByEmailFromModal: function () {
            this.mapByEmail();
            this.setOpenEmailMappingsModal(false);
        }
    }
}
</script>

<style>
.tfs4jira-mapping-value-text{
    line-height: 18px;
}
</style>

<style scoped>

.user-mapping-title{
    margin-bottom: 1em !important;
}

.mapping-no-mapped-values-container{
    margin: 20px 0;
}

.tfs4jira-mapping-table{
    margin-top: 30px;
}

.tfs4jira-mapping-container{
    margin-top: 15px;
}

.tfs4jira-mapping-table tbody{
    max-height: 310px;
    overflow-y: auto;
}

table {
    display: flex;
    flex-flow: column;
    height: 100%;
    width: 100%;
}
table tbody {
    flex: 1 1 auto;
    display: block;
    overflow-y: scroll;
}
table tbody tr {
    width: 100%;
}
table tbody tr {
    display: table;
    table-layout: fixed;
}

.scrollable-tbody {
    padding-right: 10px;
}

.mapping-no-mapped-values-container{
    margin-top: 50px;
}
</style>


// WEBPACK FOOTER //
// src/components/users/UsersMapping.vue